import gsap from "gsap";
import { clamp } from "three/src/math/MathUtils";

import useMediaStore from "../../../../common/store/useMediaStore";
import { sections, cider as _cider } from "../../../../common/config"

function sectionToHeight(cssSection: number) {
  return cssSection / 100 * window.innerHeight;
}

function getRatio(position: number, baseline: number, length: number) {
  const endline = baseline + (length * window.innerHeight);
  return clamp((position - baseline) / (endline - baseline), 0, 1);
}

/**
 * Handle update function for cider.position, used exclusively in useAnimation of WebGL cider scene
 * @param cider 
 * @param thresholds 
 * @param rotationFactor 
 * @param immediate 
 * @returns void
 */
export function handleUpdate(
  cider: THREE.Group | null,
  thresholds: number[],
  rotationFactor: number,
  immediate: boolean
) {
  if (!cider) return null;

  const threshold = useMediaStore.getState().md;
  const media = useMediaStore.getState().media;

  const { range } = _cider.position;

  const position = - cider.rotation.y * rotationFactor;

  const duration = immediate ? 0 : 0.4;
  const ease = "back.out";

  let x = 0;
  let y = 0;


  const segments = {
    rightIntoHistory: {
      baseline: thresholds[0] * 0.75,
      length: 0.5
    },
    resetFromHistory: {
      baseline: thresholds[1] + sectionToHeight(sections.height.history) * 0.1,
      length: 0.25
    },
    leftIntoFaqs: {
      baseline: thresholds[1] + sectionToHeight(sections.height.history) * 0.35,
      length: 0.25
    },
    resetFromFaqs: {
      baseline: thresholds[2] + sectionToHeight(sections.height.faqs) * 0.1,
      length: 0.25
    },
    rightIntoFlavor: {
      baseline: thresholds[2] + sectionToHeight(sections.height.faqs) * 0.35,
      length: 0.25,
    },
    intoContact: {
      baseline: thresholds[3] + sectionToHeight(sections.height.flavor) * 0.5,
      length: 0.75
    }
  }

  if (position >= segments.intoContact.baseline) {
    const ratio = getRatio(position, segments.intoContact.baseline, segments.intoContact.length);

    x = range[media].x;
    y = ratio * range[media].y;
  }
  else if (position >= segments.rightIntoFlavor.baseline) {
    if (!threshold) { x = range[media].x }
    else {
      const ratio = getRatio(position, segments.rightIntoFlavor.baseline, segments.rightIntoFlavor.length);
      x = ratio * range[media].x;
    }
  }
  else if (position >= segments.resetFromFaqs.baseline) {
    if (!threshold) { x = range[media].x }
    else {
      const ratio = getRatio(position, segments.resetFromFaqs.baseline, segments.resetFromFaqs.length);
      x = (1 - ratio) * (-range[media].x);
    }
  }
  else if (position >= segments.leftIntoFaqs.baseline) {
    if (!threshold) { x = range[media].x }
    else {
      const ratio = getRatio(position, segments.leftIntoFaqs.baseline, segments.leftIntoFaqs.length);
      x = ratio * (-range[media].x);
    }
  }
  else if (position >= segments.resetFromHistory.baseline) {
    if (!threshold) { x = range[media].x }
    else {
      const ratio = getRatio(position, segments.resetFromHistory.baseline, segments.resetFromHistory.length);
      x = (1 - ratio) * range[media].x;
    }
  }
  else if (position >= segments.rightIntoHistory.baseline) {
    const ratio = getRatio(position, segments.rightIntoHistory.baseline, segments.rightIntoHistory.length);

    x = ratio * range[media].x;
  }

  gsap.to(cider.position, { duration, ease, x, y });
}